(function () {
  const STORAGE_KEY = 'superior-mix-and-match-builder-v1';
  const STATE_EVENT = 'superior:mix-and-match:state';

  function isMixedCatalogBuilderEnabledOnPage() {
    const mixedCatalogElements = Array.from(document.querySelectorAll('mixed-catalog-grid'));
    if (!mixedCatalogElements.length) return true;

    return mixedCatalogElements.some((element) => element.dataset.bundleBuilderEnabled === 'true');
  }

  function getActiveBuilderElement() {
    const builderElement = document.querySelector('mix-and-match-builder[data-template-enabled="true"]');
    if (!builderElement) return null;

    return isMixedCatalogBuilderEnabledOnPage() ? builderElement : null;
  }

  function parsePositiveInteger(value) {
    const parsedValue = Number.parseInt(String(value ?? ''), 10);
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
  }

  function parsePositivePercentage(value) {
    const parsedValue = Number.parseFloat(String(value ?? ''));
    if (!Number.isFinite(parsedValue) || parsedValue <= 0) return null;
    return Math.min(parsedValue, 100);
  }

  class SuperiorMixAndMatchStore {
    constructor() {
      this.storageKey = STORAGE_KEY;
      this.defaultMoq = 15;
      this.sequence = 0;
      this.builderElement = null;
      this.subscribers = new Set();
      this.state = {
        open: false,
        items: [],
        agentContext: null,
        checkoutPending: false,
      };
      this.load();
    }

    normalizeContext(context) {
      const origin = String(context?.origin || '').trim();
      if (!origin) return null;

      const moq = parsePositiveInteger(context?.moq);
      const discount = parsePositivePercentage(context?.discount ?? context?.discountOverride);
      const normalizedContext = { origin };

      if (moq) normalizedContext.moq = moq;
      if (discount) normalizedContext.discount = discount;

      return normalizedContext;
    }

    normalizeItem(item) {
      const id = String(item?.id || '').trim();
      const quantity = parsePositiveInteger(item?.quantity);

      if (!id || !quantity) return null;

      const parsedOrder = Number.parseInt(String(item?.order ?? ''), 10);
      const order = Number.isFinite(parsedOrder) ? parsedOrder : this.sequence++;
      if (order >= this.sequence) {
        this.sequence = order + 1;
      }

      return {
        id,
        quantity,
        title: String(item?.title || 'Bundle item').trim() || 'Bundle item',
        image: String(item?.image || '').trim(),
        order,
      };
    }

    load() {
      let parsedState = null;

      try {
        parsedState = JSON.parse(window.localStorage.getItem(this.storageKey) || 'null');
      } catch (error) {
        parsedState = null;
      }

      const nextItems = Array.isArray(parsedState?.items)
        ? parsedState.items.map((item) => this.normalizeItem(item)).filter(Boolean)
        : [];

      const nextContext = this.normalizeContext(parsedState?.agentContext);
      const parsedSequence = Number.parseInt(String(parsedState?.sequence ?? ''), 10);
      const highestOrder = nextItems.reduce((maxOrder, item) => Math.max(maxOrder, item.order), -1);

      this.sequence = Number.isFinite(parsedSequence)
        ? Math.max(parsedSequence, highestOrder + 1, 0)
        : Math.max(highestOrder + 1, 0);

      this.state = {
        open: Boolean(parsedState?.open),
        items: nextItems,
        agentContext: nextContext,
        checkoutPending: false,
      };
    }

    persist() {
      try {
        window.localStorage.setItem(
          this.storageKey,
          JSON.stringify({
            open: this.state.open,
            items: this.state.items,
            agentContext: this.state.agentContext,
            sequence: this.sequence,
          })
        );
      } catch (error) {
      }
    }

    getState() {
      const items = [...this.state.items]
        .sort((firstItem, secondItem) => firstItem.order - secondItem.order)
        .map((item) => ({ ...item }));
      const agentContext = this.state.agentContext ? { ...this.state.agentContext } : null;
      const effectiveMoq = parsePositiveInteger(agentContext?.moq) || this.defaultMoq;
      const totalQuantity = items.reduce((quantity, item) => quantity + item.quantity, 0);

      return {
        open: this.state.open,
        items,
        agentContext,
        effectiveMoq,
        totalQuantity,
        checkoutPending: this.state.checkoutPending,
        isReady: totalQuantity >= effectiveMoq,
      };
    }

    emit() {
      const state = this.getState();
      this.persist();

      this.subscribers.forEach((callback) => {
        try {
          callback(state);
        } catch (error) {
          console.error(error);
        }
      });

      document.dispatchEvent(new CustomEvent(STATE_EVENT, { detail: state }));
    }

    subscribe(callback) {
      this.subscribers.add(callback);
      callback(this.getState());

      return () => {
        this.subscribers.delete(callback);
      };
    }

    registerBuilder(element, options = {}) {
      this.builderElement = element;

      if (options.storageKey && options.storageKey !== this.storageKey) {
        this.storageKey = options.storageKey;
        this.load();
      }

      const defaultMoq = parsePositiveInteger(options.defaultMoq);
      if (defaultMoq) {
        this.defaultMoq = defaultMoq;
      }

      this.applyPageContext(options.pageContext, { silent: true });
      this.emit();
    }

    applyPageContext(context, options = {}) {
      const normalizedContext = this.normalizeContext(context);
      let changed = false;

      if (normalizedContext) {
        const currentContext = this.state.agentContext || null;

        if (
          currentContext?.origin !== normalizedContext.origin ||
          currentContext?.moq !== normalizedContext.moq ||
          currentContext?.discount !== normalizedContext.discount
        ) {
          this.state.agentContext = normalizedContext;
          changed = true;
        }
      } else if (!this.state.items.length && this.state.agentContext) {
        this.state.agentContext = null;
        changed = true;
      }

      if (changed && !options.silent) {
        this.emit();
      }

      return changed;
    }

    toggleOpen(forceState = null, context = null) {
      this.applyPageContext(context, { silent: true });
      const nextOpenState = typeof forceState === 'boolean' ? forceState : !this.state.open;

      if (this.state.open === nextOpenState) {
        this.emit();
        return;
      }

      this.state.open = nextOpenState;
      this.emit();
    }

    setItemQuantity(variantId, quantity, productData = {}, options = {}) {
      const normalizedVariantId = String(variantId || '').trim();
      const normalizedQuantity = parsePositiveInteger(quantity) || 0;

      if (!normalizedVariantId) return;

      this.applyPageContext(options.pageContext, { silent: true });

      const nextItems = [...this.state.items];
      const itemIndex = nextItems.findIndex((item) => item.id === normalizedVariantId);
      const existingItem = itemIndex >= 0 ? nextItems[itemIndex] : null;

      if (normalizedQuantity <= 0) {
        if (itemIndex >= 0) {
          nextItems.splice(itemIndex, 1);
        }
      } else {
        const nextItem = {
          id: normalizedVariantId,
          quantity: normalizedQuantity,
          title: String(productData?.title || existingItem?.title || 'Bundle item').trim() || 'Bundle item',
          image: String(productData?.image || existingItem?.image || '').trim(),
          order: existingItem?.order ?? this.sequence++,
        };

        if (itemIndex >= 0) {
          nextItems.splice(itemIndex, 1, nextItem);
        } else {
          nextItems.push(nextItem);
        }
      }

      this.state.items = nextItems;
      this.emit();
    }

    addItem(variantId, quantity, productData = {}, options = {}) {
      const currentQuantity = this.getItemQuantity(variantId);
      const quantityDelta = parsePositiveInteger(quantity) || 1;
      this.setItemQuantity(variantId, currentQuantity + quantityDelta, productData, options);
    }

    removeItem(variantId) {
      this.setItemQuantity(variantId, 0);
    }

    getItemQuantity(variantId) {
      return this.state.items.find((item) => item.id === String(variantId || '').trim())?.quantity || 0;
    }

    clear(options = {}) {
      this.state.items = [];
      this.state.open = false;
      this.state.checkoutPending = false;
      this.sequence = 0;

      if (!options.preserveContext) {
        this.state.agentContext = null;
      }

      this.emit();
    }

    getVisualTarget() {
      if (this.builderElement && typeof this.builderElement.getVisualTarget === 'function') {
        return this.builderElement.getVisualTarget();
      }

      return null;
    }

    formatStatus(totalQuantity, effectiveMoq) {
      if (!totalQuantity) {
        return `Add ${effectiveMoq} ${effectiveMoq === 1 ? 'item' : 'items'} to complete your bundle`;
      }

      if (totalQuantity < effectiveMoq) {
        const remainingQuantity = effectiveMoq - totalQuantity;
        return `Add ${remainingQuantity} more ${remainingQuantity === 1 ? 'item' : 'items'} to complete your bundle`;
      }

      if (totalQuantity === effectiveMoq) {
        return 'MOQ reached. Your bundle is ready for checkout';
      }

      const extraQuantity = totalQuantity - effectiveMoq;
      return `${extraQuantity} extra ${extraQuantity === 1 ? 'item' : 'items'} added above the MOQ`;
    }

    formatStatusMarkup(totalQuantity, effectiveMoq) {
      if (!totalQuantity) {
        return `Add <strong>${effectiveMoq}</strong> ${effectiveMoq === 1 ? 'item' : 'items'} to complete your bundle`;
      }

      if (totalQuantity < effectiveMoq) {
        const remainingQuantity = effectiveMoq - totalQuantity;
        return `Add <strong>${remainingQuantity}</strong> more ${remainingQuantity === 1 ? 'item' : 'items'} to complete your bundle`;
      }

      if (totalQuantity === effectiveMoq) {
        return 'MOQ reached. Your bundle is ready for checkout';
      }

      const extraQuantity = totalQuantity - effectiveMoq;
      return `<strong>${extraQuantity}</strong> extra ${extraQuantity === 1 ? 'item' : 'items'} added above the MOQ`;
    }

    getAgentLineItemProperties() {
      if (!this.state.agentContext?.origin) return null;

      const properties = {
        _agent_origin: this.state.agentContext.origin,
      };

      if (parsePositiveInteger(this.state.agentContext.moq)) {
        properties._agent_shop_moq = `${this.state.agentContext.moq}`;
      }

      if (parsePositivePercentage(this.state.agentContext.discount)) {
        properties._agent_shop_discount = `${this.state.agentContext.discount}`;
      }

      return properties;
    }

    getCartElement() {
      return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    }

    getCartSectionsToRender(cart = this.getCartElement()) {
      if (!cart) return [];

      if (cart.tagName === 'CART-DRAWER') {
        const cartItems = cart.querySelector('cart-drawer-items');

        if (cartItems && typeof cartItems.getSectionsToRender === 'function') {
          return cartItems.getSectionsToRender();
        }
      }

      if (typeof cart.getSectionsToRender === 'function') {
        return cart.getSectionsToRender();
      }

      return [];
    }

    getSectionsUrl() {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete('section_id');
      return `${currentUrl.pathname}${currentUrl.search}`;
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
      const source = parsedHtml.querySelector(selector) || parsedHtml.body;

      return source.innerHTML;
    }

    syncCartDrawerState(sectionHtml, cart = this.getCartElement()) {
      if (!sectionHtml || !cart || cart.tagName !== 'CART-DRAWER') return;

      const parsedHtml = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const sourceCartDrawer = parsedHtml.querySelector('cart-drawer');
      const sourceCartDrawerItems = parsedHtml.querySelector('cart-drawer-items');

      if (sourceCartDrawer) {
        cart.classList.toggle('is-empty', sourceCartDrawer.classList.contains('is-empty'));
        return;
      }

      if (sourceCartDrawerItems) {
        cart.classList.toggle('is-empty', sourceCartDrawerItems.classList.contains('is-empty'));
      }
    }

    updateCartUi(sections, cart = this.getCartElement()) {
      if (!sections) return false;

      const sectionsToRender = this.getCartSectionsToRender(cart);

      if (!sectionsToRender.length) {
        if (sections['cart-icon-bubble'] && typeof updateCartIconBubble === 'function') {
          updateCartIconBubble(sections['cart-icon-bubble']);
          return true;
        }

        return false;
      }

      sectionsToRender.forEach((section) => {
        const sectionId = section.section || section.id;
        const sectionHtml = sections[sectionId] || sections[section.id];
        if (!sectionHtml) return;

        if (sectionId === 'cart-drawer') {
          this.syncCartDrawerState(sectionHtml, cart);
        }

        if (section.id === 'cart-icon-bubble') {
          if (typeof updateCartIconBubble === 'function') {
            updateCartIconBubble(sectionHtml, section.selector || '.shopify-section');
          }
          return;
        }

        const sectionRoot = document.getElementById(section.id);
        const sectionElement =
          sectionRoot && section.selector ? sectionRoot.querySelector(section.selector) || sectionRoot : sectionRoot;
        const targetElement = sectionElement || (section.selector ? document.querySelector(section.selector) : null);
        if (!targetElement) return;

        targetElement.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
      });

      return true;
    }

    async refreshCartUi(parsedState = null) {
      const cart = this.getCartElement();

      if (parsedState?.sections && this.updateCartUi(parsedState.sections, cart)) {
        return;
      }

      const sectionUrl = new URL(window.location.href);
      const sectionIds = this.getCartSectionsToRender(cart)
        .map((section) => section.section || section.id)
        .filter(Boolean);

      if (!sectionIds.length) {
        sectionIds.push('cart-icon-bubble');
      }

      sectionUrl.searchParams.set('sections', [...new Set(sectionIds)].join(','));
      sectionUrl.searchParams.delete('section_id');

      const response = await fetch(sectionUrl.toString(), {
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });
      const sections = await response.json();

      this.updateCartUi(sections, cart);
    }

    async addBundleItems(items, options = {}) {
      const { includeSections = true } = options;
      const cart = this.getCartElement();

      if (!items.length) {
        throw new Error('No bundle items found');
      }

      const payload = {
        items: items.map((item) => {
          const cartItem = {
            id: item.id,
            quantity: item.quantity || 1,
          };
          const agentLineItemProperties = this.getAgentLineItemProperties();

          if (agentLineItemProperties) {
            cartItem.properties = { ...agentLineItemProperties };
          }

          return cartItem;
        }),
      };

      const sectionsToRender = includeSections ? this.getCartSectionsToRender(cart) : [];

      if (sectionsToRender.length) {
        payload.sections = sectionsToRender.map((section) => section.section || section.id);
        payload.sections_url = this.getSectionsUrl();
      }

      const addUrl = window.routes?.cart_add_url ? `${window.routes.cart_add_url}.js` : '/cart/add.js';
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || 'Add failed');
      }

      await this.refreshCartUi(data);
    }

    async checkout() {
      const state = this.getState();

      if (!state.items.length) {
        return {
          ok: false,
          message: this.formatStatus(state.totalQuantity, state.effectiveMoq),
        };
      }

      if (!state.isReady) {
        return {
          ok: false,
          message: this.formatStatus(state.totalQuantity, state.effectiveMoq),
        };
      }

      if (this.state.checkoutPending) {
        return {
          ok: false,
          message: 'Loading...',
        };
      }

      this.state.checkoutPending = true;
      this.emit();

      try {
        await this.addBundleItems(
          state.items.map((item) => ({ id: item.id, quantity: item.quantity })),
          { includeSections: false }
        );

        this.clear();
        window.location.href = window.routes?.cart_url || '/cart';

        return { ok: true };
      } catch (error) {
        this.state.checkoutPending = false;
        this.emit();
        throw error;
      }
    }
  }

  class MixAndMatchBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initializeOnReady = this.initializeOnReady || this.initialize.bind(this);

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.initializeOnReady, { once: true });
        return;
      }

      this.initialize();
    }

    disconnectedCallback() {
      document.removeEventListener('DOMContentLoaded', this.initializeOnReady);
      this.unsubscribeStore?.();
      if (this.slots) {
        this.slots.removeEventListener('scroll', this.handleSlotsScroll);
      }
      if (this.noticeTimer) {
        clearTimeout(this.noticeTimer);
      }
    }

    initialize() {
      if (this.initialized || !this.isConnected) return;
      this.initialized = true;

      this.store = window.SuperiorMixAndMatch;
      if (!this.store) return;

      this.launcher = this.querySelector('[data-mix-match-launcher]');
      this.launcherLabel = this.querySelector('[data-mix-match-launcher-label]');
      this.launcherCount = this.querySelector('[data-mix-match-launcher-count]');
      this.tray = this.querySelector('[data-mix-match-tray]');
      this.slotsWrap = this.querySelector('[data-mix-match-slots-wrap]');
      this.slots = this.querySelector('[data-mix-match-slots]');
      this.scrollButtons = Array.from(this.querySelectorAll('[data-mix-match-scroll-direction]'));
      this.status = this.querySelector('[data-mix-match-status]');
      this.checkoutButton = this.querySelector('[data-mix-match-checkout]');
      this.checkoutLabel = this.querySelector('[data-mix-match-checkout-label]');
      this.notice = this.querySelector('[data-mix-match-notice]');
      this.launcherLabelText = this.dataset.launcherLabel || 'Mix & Match your own bundle';
      this.checkoutLabelText = this.dataset.checkoutLabel || 'Checkout bundle';
      this.handleStateChange = this.handleStateChange.bind(this);
      this.handleSlotsScroll = this.updateScrollControls.bind(this);

      if (this.checkoutButton) {
        this.checkoutButton.addEventListener('click', async () => {
          try {
            const result = await this.store.checkout();
            if (result && result.ok === false && result.message) {
              this.showNotice(result.message);
            }
          } catch (error) {
            this.showNotice(error?.message || 'Unable to add bundle to cart');
          }
        });
      }

      if (this.slots) {
        this.slots.addEventListener('scroll', this.handleSlotsScroll, { passive: true });
      }

      this.scrollButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const direction = Number.parseInt(button.dataset.mixMatchScrollDirection, 10) || 0;
          this.scrollSlots(direction);
        });
      });

      this.addEventListener('click', (event) => {
        const removeButton = event.target.closest('[data-remove-mix-match-item]');
        if (!removeButton) return;

        event.preventDefault();
        this.store.removeItem(removeButton.dataset.removeMixMatchItem);
      });

      this.store.registerBuilder(this, {
        storageKey: this.dataset.storageKey || STORAGE_KEY,
        defaultMoq: this.dataset.defaultMoq,
        pageContext: this.getPageContext(),
      });

      if (this.shouldAutoOpen()) {
        this.store.toggleOpen(true, this.getPageContext());
      }

      this.unsubscribeStore = this.store.subscribe(this.handleStateChange);
    }

    shouldAutoOpen() {
      return this.dataset.autoOpen === 'true' && this.isAvailableOnCurrentPage();
    }

    isAvailableOnCurrentPage() {
      return this.dataset.templateEnabled === 'true' && isMixedCatalogBuilderEnabledOnPage();
    }

    getPageContext() {
      const origin = this.dataset.agentOrigin || '';
      if (!origin) return null;

      return {
        origin,
        moq: this.dataset.agentShopMoq || '',
        discount: this.dataset.agentShopDiscount || '',
      };
    }

    getVisualTarget() {
      return this.slotsWrap || this.tray || null;
    }

    handleStateChange(state) {
      this.currentState = state;
      const shouldShow = this.isAvailableOnCurrentPage();

      if (!shouldShow && state.open) {
        this.store.toggleOpen(false, this.getPageContext());
        return;
      }

      this.hidden = !shouldShow;
      this.classList.toggle('is-open', shouldShow && Boolean(state.open));

      if (this.tray) {
        this.tray.setAttribute('aria-hidden', shouldShow && state.open ? 'false' : 'true');
      }

      if (!shouldShow) return;

      const countLabel = `${state.totalQuantity}/${state.effectiveMoq}`;

      if (this.launcherLabel) {
        this.launcherLabel.textContent = this.launcherLabelText;
      }

      if (this.launcherCount) {
        this.launcherCount.textContent = countLabel;
      }

      if (this.status) {
        this.status.innerHTML = this.store.formatStatusMarkup(state.totalQuantity, state.effectiveMoq);
      }

      if (this.checkoutButton) {
        this.checkoutButton.disabled = !state.isReady || state.checkoutPending;
        this.checkoutButton.classList.toggle('is-ready', state.isReady && !state.checkoutPending);
      }

      if (this.checkoutLabel) {
        this.checkoutLabel.textContent = state.checkoutPending ? 'Loading...' : this.checkoutLabelText;
      }

      this.renderSlots(state.items);
    }

    renderSlots(items) {
      if (!this.slots) return;

      const filledSlots = items
        .map((item) => {
          const imageMarkup = item.image
            ? `<img src="${this.escapeHtml(item.image)}" alt="${this.escapeHtml(item.title)}" loading="lazy" width="120" height="120">`
            : `<div class="mix-and-match-builder__slot-placeholder">Item</div>`;

          return `
            <article class="mix-and-match-builder__slot mix-and-match-builder__slot--filled" data-mix-match-slot-variant-id="${this.escapeHtml(item.id)}">
              <button class="mix-and-match-builder__slot-remove" type="button" data-remove-mix-match-item="${this.escapeHtml(item.id)}" aria-label="Remove ${this.escapeHtml(item.title)} from bundle">&times;</button>
              <div class="mix-and-match-builder__slot-media">
                ${imageMarkup}
                <span class="mix-and-match-builder__slot-qty">${item.quantity}</span>
              </div>
              <p class="mix-and-match-builder__slot-name">${this.escapeHtml(item.title)}</p>
            </article>
          `;
        })
        .join('');

      const nextSlotNumber = items.length + 1;
      const emptySlot = `
        <div class="mix-and-match-builder__slot mix-and-match-builder__slot--empty" data-mix-match-slot-empty>
          <span class="mix-and-match-builder__slot-number">${nextSlotNumber}</span>
          <span class="mix-and-match-builder__slot-empty-label">Open slot</span>
        </div>
      `;

      this.slots.classList.toggle('is-open-slot-centered', items.length === 0);
      this.slots.innerHTML = `${filledSlots}${emptySlot}`;

      window.requestAnimationFrame(() => {
        this.centerOpenSlot(items.length ? 'smooth' : 'auto');
        this.updateScrollControls();
      });
    }

    scrollSlots(direction) {
      if (!this.slots || !direction) return;

      const scrollDistance = Math.max(this.slots.clientWidth * 0.72, 180);
      this.slots.scrollBy({
        left: direction * scrollDistance,
        behavior: 'smooth',
      });
    }

    updateScrollControls() {
      if (!this.slots || !this.scrollButtons.length) return;

      const hasOverflow = this.slots.scrollWidth - this.slots.clientWidth > 8;
      const canScrollLeft = hasOverflow && this.slots.scrollLeft > 8;
      const canScrollRight = hasOverflow && this.slots.scrollLeft + this.slots.clientWidth < this.slots.scrollWidth - 8;

      if (this.slotsWrap) {
        this.slotsWrap.classList.toggle('can-scroll-left', canScrollLeft);
        this.slotsWrap.classList.toggle('can-scroll-right', canScrollRight);
      }

      this.scrollButtons.forEach((button) => {
        const direction = Number.parseInt(button.dataset.mixMatchScrollDirection, 10) || 0;
        button.hidden = !hasOverflow;
        button.disabled = direction < 0 ? !canScrollLeft : !canScrollRight;
      });
    }

    centerOpenSlot(behavior = 'smooth') {
      if (!this.slots) return;

      const openSlot = this.slots.querySelector('[data-mix-match-slot-empty]');
      if (!openSlot) return;

      const maxScrollLeft = this.slots.scrollWidth - this.slots.clientWidth;
      if (maxScrollLeft <= 0) return;

      const slotsRect = this.slots.getBoundingClientRect();
      const openSlotRect = openSlot.getBoundingClientRect();
      const nextScrollLeft = this.slots.scrollLeft + (openSlotRect.left + openSlotRect.width / 2) - (slotsRect.left + slotsRect.width / 2);
      const clampedScrollLeft = Math.max(0, Math.min(maxScrollLeft, nextScrollLeft));

      if (behavior === 'auto') {
        this.slots.scrollLeft = clampedScrollLeft;
        return;
      }

      this.slots.scrollTo({
        left: clampedScrollLeft,
        behavior,
      });
    }

    showNotice(message) {
      if (!this.notice) return;

      this.notice.textContent = message;
      this.notice.classList.add('is-visible');

      if (this.noticeTimer) {
        clearTimeout(this.noticeTimer);
      }

      this.noticeTimer = window.setTimeout(() => {
        this.notice.classList.remove('is-visible');
      }, 2200);
    }

    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  class MixAndMatchProductControls extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initializeOnReady = this.initializeOnReady || this.initialize.bind(this);

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.initializeOnReady, { once: true });
        return;
      }

      this.initialize();
    }

    disconnectedCallback() {
      document.removeEventListener('DOMContentLoaded', this.initializeOnReady);
      this.unsubscribeStore?.();
      this.variantChangeUnsubscriber?.();
      if (this.form) {
        this.form.removeEventListener('change', this.handleFormChange);
        this.form.removeEventListener('input', this.handleFormChange);
      }
    }

    initialize() {
      if (this.initialized || !this.isConnected) return;
      this.initialized = true;

      this.store = window.SuperiorMixAndMatch;
      if (!this.store) {
        this.hidden = true;
        return;
      }

      this.form = this.closest('form');
      this.addButton = this.querySelector('[data-mix-match-add]');
      this.openButton = this.querySelector('[data-mix-match-open]');
      this.openCount = this.querySelector('[data-mix-match-open-count]');
      this.stepper = this.querySelector('[data-mix-match-stepper]');
      this.quantityNode = this.querySelector('[data-mix-match-quantity]');
      this.incrementButton = this.querySelector('[data-mix-match-increment]');
      this.decrementButton = this.querySelector('[data-mix-match-decrement]');
      this.currentState = this.store.getState();
      this.handleStateChange = this.handleStateChange.bind(this);
      this.handleFormChange = this.render.bind(this);

      if (this.addButton) {
        this.addButton.addEventListener('click', () => {
          const variantId = this.getVariantId();
          if (!variantId) return;

          this.store.addItem(variantId, this.getSelectedQuantity(), this.getProductData(), {
            pageContext: this.getPageContext(),
          });
          this.store.toggleOpen(true, this.getPageContext());
        });
      }

      if (this.openButton) {
        this.openButton.addEventListener('click', () => {
          this.store.toggleOpen(true, this.getPageContext());
        });
      }

      if (this.incrementButton) {
        this.incrementButton.addEventListener('click', () => {
          const variantId = this.getVariantId();
          if (!variantId) return;
          this.store.setItemQuantity(
            variantId,
            this.store.getItemQuantity(variantId) + 1,
            this.getProductData(),
            { pageContext: this.getPageContext() }
          );
        });
      }

      if (this.decrementButton) {
        this.decrementButton.addEventListener('click', () => {
          const variantId = this.getVariantId();
          if (!variantId) return;
          this.store.setItemQuantity(
            variantId,
            this.store.getItemQuantity(variantId) - 1,
            this.getProductData(),
            { pageContext: this.getPageContext() }
          );
        });
      }

      if (this.form) {
        this.form.addEventListener('change', this.handleFormChange);
        this.form.addEventListener('input', this.handleFormChange);
      }

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => {
          this.render();
        });
      }

      this.unsubscribeStore = this.store.subscribe(this.handleStateChange);
    }

    handleStateChange(state) {
      this.currentState = state;
      this.render();
    }

    getVariantId() {
      return String(this.form?.querySelector('[name="id"]')?.value || '').trim();
    }

    getSelectedQuantity() {
      return parsePositiveInteger(this.form?.querySelector('[name="quantity"]')?.value) || 1;
    }

    isVariantUnavailable() {
      const variantInput = this.form?.querySelector('[name="id"]');
      const submitButton = this.closest('product-form')?.querySelector('[type="submit"]');
      return Boolean(variantInput?.disabled || submitButton?.disabled);
    }

    getProductData() {
      return {
        title: this.dataset.productTitle || 'Bundle item',
        image: this.dataset.productImage || '',
      };
    }

    getPageContext() {
      const stateContext = this.currentState?.agentContext || this.store?.getState?.().agentContext || null;
      if (!stateContext?.origin) return null;

      return {
        origin: stateContext.origin,
        moq: stateContext.moq || '',
        discount: stateContext.discount || '',
      };
    }

    render() {
      const state = this.currentState || this.store.getState();
      const variantId = this.getVariantId();
      const variantQuantity = variantId ? state.items.find((item) => item.id === variantId)?.quantity || 0 : 0;
      const isUnavailable = this.isVariantUnavailable();
      const activeBuilderElement = getActiveBuilderElement();
      const shouldShow = Boolean(activeBuilderElement);

      this.hidden = !shouldShow;
      if (!shouldShow) return;

      if (this.openCount) {
        this.openCount.textContent = `${state.totalQuantity}/${state.effectiveMoq}`;
      }

      if (this.addButton) {
        this.addButton.disabled = !variantId || isUnavailable;
      }

      if (this.openButton) {
        this.openButton.disabled = false;
      }

      if (this.stepper) {
        this.stepper.hidden = variantQuantity < 1;
      }

      if (this.quantityNode) {
        this.quantityNode.textContent = variantQuantity > 0 ? `${variantQuantity} in bundle` : '0 in bundle';
      }

      if (this.decrementButton) {
        this.decrementButton.disabled = variantQuantity < 1;
      }

      if (this.incrementButton) {
        this.incrementButton.disabled = !variantId || isUnavailable;
      }
    }
  }

  class MixAndMatchInlineControls extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initializeOnReady = this.initializeOnReady || this.initialize.bind(this);

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.initializeOnReady, { once: true });
        return;
      }

      this.initialize();
    }

    disconnectedCallback() {
      document.removeEventListener('DOMContentLoaded', this.initializeOnReady);
      this.unsubscribeStore?.();
      this.variantChangeUnsubscriber?.();
      if (this.form) {
        this.form.removeEventListener('submit', this.handleFormSubmit, true);
        this.form.removeEventListener('change', this.handleFormChange);
        this.form.removeEventListener('input', this.handleFormChange);
      }
    }

    initialize() {
      if (this.initialized || !this.isConnected) return;
      this.initialized = true;

      this.inlineEnabled = this.dataset.inlineEnabled === 'true';
      if (!this.inlineEnabled) return;

      this.store = window.SuperiorMixAndMatch;
      if (!this.store) return;

      this.form = this.closest('form');
      this.submitMode = this.dataset.submitMode === 'replace' ? 'replace' : 'add';
      this.submitButton = this.querySelector('[data-mix-match-inline-submit]');
      this.defaultLabelNode = this.querySelector('[data-mix-match-inline-default-label]');
      this.bundleLabelNode = this.querySelector('[data-mix-match-inline-bundle-label]');
      this.stepper = this.querySelector('[data-mix-match-inline-stepper]');
      this.quantityNode = this.querySelector('[data-mix-match-inline-quantity]');
      this.incrementButton = this.querySelector('[data-mix-match-inline-increment]');
      this.decrementButton = this.querySelector('[data-mix-match-inline-decrement]');
      this.currentState = this.store.getState();
      this.handleStateChange = this.handleStateChange.bind(this);
      this.handleFormChange = this.render.bind(this);
      this.handleFormSubmit = this.handleFormSubmit.bind(this);

      if (this.incrementButton) {
        this.incrementButton.addEventListener('click', () => {
          const variantId = this.getVariantId();
          if (!variantId) return;

          this.store.setItemQuantity(
            variantId,
            this.store.getItemQuantity(variantId) + 1,
            this.getProductData(),
            { pageContext: this.getPageContext() }
          );
        });
      }

      if (this.decrementButton) {
        this.decrementButton.addEventListener('click', () => {
          const variantId = this.getVariantId();
          if (!variantId) return;

          this.store.setItemQuantity(
            variantId,
            this.store.getItemQuantity(variantId) - 1,
            this.getProductData(),
            { pageContext: this.getPageContext() }
          );
        });
      }

      if (this.form) {
        this.form.addEventListener('submit', this.handleFormSubmit, true);
        this.form.addEventListener('change', this.handleFormChange);
        this.form.addEventListener('input', this.handleFormChange);
      }

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => {
          this.render();
        });
      }

      this.unsubscribeStore = this.store.subscribe(this.handleStateChange);
    }

    handleStateChange(state) {
      this.currentState = state;
      this.render();
    }

    handleFormSubmit(event) {
      const state = this.currentState || this.store?.getState();
      if (!this.isInlineBundleModeActive(state)) return;

      const variantId = this.getVariantId();
      if (!variantId || this.isVariantUnavailable()) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const selectedQuantity = this.getSelectedQuantity();
      const productData = this.getProductData();
      const pageContext = this.getPageContext();

      if (this.submitMode === 'replace') {
        this.store.setItemQuantity(variantId, selectedQuantity, productData, {
          pageContext,
        });
      } else {
        this.store.addItem(variantId, selectedQuantity, productData, {
          pageContext,
        });
      }

      this.store.toggleOpen(true, this.getPageContext());
    }

    getVariantId() {
      return String(this.form?.querySelector('[name="id"]')?.value || '').trim();
    }

    getSelectedQuantity() {
      return parsePositiveInteger(this.form?.querySelector('[name="quantity"]')?.value) || 1;
    }

    isVariantUnavailable() {
      const variantInput = this.form?.querySelector('[name="id"]');
      return Boolean(!variantInput || variantInput.disabled);
    }

    getProductData() {
      return {
        title: this.dataset.productTitle || 'Bundle item',
        image: this.dataset.productImage || '',
      };
    }

    getPageContext() {
      const stateContext = this.currentState?.agentContext || this.store?.getState?.().agentContext || null;
      if (!stateContext?.origin) return null;

      return {
        origin: stateContext.origin,
        moq: stateContext.moq || '',
        discount: stateContext.discount || '',
      };
    }

    isInlineBundleModeActive(state = this.currentState || this.store?.getState()) {
      return Boolean(this.inlineEnabled && getActiveBuilderElement() && state?.open);
    }

    render() {
      const state = this.currentState || this.store?.getState();
      if (!state) return;

      const variantId = this.getVariantId();
      const variantQuantity = variantId ? state.items.find((item) => item.id === variantId)?.quantity || 0 : 0;
      const isUnavailable = this.isVariantUnavailable();
      const inlineBuilderOpen = Boolean(this.inlineEnabled && getActiveBuilderElement() && state?.open);
      const inlineBundleModeActive = this.isInlineBundleModeActive(state) && Boolean(variantId) && !isUnavailable;

      this.classList.toggle('is-mix-and-match-active', inlineBuilderOpen);

      if (this.stepper) {
        this.stepper.hidden = !this.isInlineBundleModeActive(state);
      }

      if (this.quantityNode) {
        this.quantityNode.textContent = `${variantQuantity} in bundle`;
      }

      if (this.decrementButton) {
        this.decrementButton.disabled = !this.isInlineBundleModeActive(state) || variantQuantity < 1;
      }

      if (this.incrementButton) {
        this.incrementButton.disabled = !this.isInlineBundleModeActive(state) || !variantId || isUnavailable;
      }

      if (this.defaultLabelNode) {
        this.defaultLabelNode.hidden = inlineBundleModeActive;
      }

      if (this.bundleLabelNode) {
        this.bundleLabelNode.hidden = !inlineBundleModeActive;
      }
    }
  }

  window.SuperiorMixAndMatch = window.SuperiorMixAndMatch || new SuperiorMixAndMatchStore();

  if (!customElements.get('mix-and-match-builder')) {
    customElements.define('mix-and-match-builder', MixAndMatchBuilder);
  }

  if (!customElements.get('mix-and-match-product-controls')) {
    customElements.define('mix-and-match-product-controls', MixAndMatchProductControls);
  }

  if (!customElements.get('mix-and-match-inline-controls')) {
    customElements.define('mix-and-match-inline-controls', MixAndMatchInlineControls);
  }
})();